import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Music, Upload, HelpCircle, FileAudio, FileText as FilePdfIcon, Send } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Public-facing Music & Content Sheet Submission form. Replaces the
 * placeholder card on the competition page.
 *
 * Flow:
 *   1. Pick Club → loads entries for that club in music-required grades.
 *   2. Pick Skater/Pair/Team → loads round metadata + filename preview.
 *   3. For each round: upload .mp3 music + .pdf content sheet.
 *   4. Click Submit → confirm dialog listing renamed files → POST multipart.
 */
export default function MusicSubmissionForm({ pageId }) {
  const [clubs, setClubs] = useState([]);
  const [entries, setEntries] = useState([]);
  const [detail, setDetail] = useState(null);   // entry metadata + rounds

  const [clubId, setClubId] = useState('');
  const [entryId, setEntryId] = useState('');
  // files keyed by round key → { music: File|null, content: File|null }
  const [files, setFiles] = useState({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Load clubs once.
  useEffect(() => {
    if (!pageId) return;
    axios.get(`${API}/public/competition/${pageId}/music/clubs`)
      .then(res => setClubs(res.data.clubs || []))
      .catch(() => setClubs([]));
  }, [pageId]);

  // When club changes, load entries for that club.
  useEffect(() => {
    if (!pageId || !clubId) {
      setEntries([]);
      setEntryId('');
      return;
    }
    axios.get(`${API}/public/competition/${pageId}/music/entries`, {
      params: { club_id: clubId },
    })
      .then(res => {
        setEntries(res.data.entries || []);
        setEntryId('');
      })
      .catch(() => setEntries([]));
  }, [pageId, clubId]);

  // When entry changes, load its metadata.
  useEffect(() => {
    if (!pageId || !entryId) {
      setDetail(null);
      setFiles({});
      return;
    }
    setLoadingDetail(true);
    axios.get(`${API}/public/competition/${pageId}/music/entry/${entryId}`)
      .then(res => {
        setDetail(res.data);
        const blank = {};
        (res.data.rounds || []).forEach(r => {
          blank[r.key] = { music: null, content: null };
        });
        setFiles(blank);
      })
      .catch((err) => {
        setDetail(null);
        setFiles({});
        toast.error(err.response?.data?.detail || 'Failed to load entry details');
      })
      .finally(() => setLoadingDetail(false));
  }, [pageId, entryId]);

  const allUploaded = useMemo(() => {
    if (!detail) return false;
    return (detail.rounds || []).every(r =>
      files[r.key]?.music && (!detail.content_sheet_required || files[r.key]?.content)
    );
  }, [detail, files]);

  const updateFile = (roundKey, slot, file) => {
    setFiles(prev => ({
      ...prev,
      [roundKey]: { ...(prev[roundKey] || {}), [slot]: file },
    }));
  };

  const previewList = useMemo(() => {
    if (!detail) return [];
    const rows = [];
    (detail.rounds || []).forEach(r => {
      const prev = detail.filename_preview?.[r.key] || {};
      rows.push({ round: r.content_type, kind: 'Music', filename: prev.music });
      if (detail.content_sheet_required) {
        rows.push({ round: r.content_type, kind: 'Content Sheet', filename: prev.content_sheet });
      }
    });
    return rows;
  }, [detail]);

  const runSubmit = async () => {
    if (!detail) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('entry_id', entryId);
      (detail.rounds || []).forEach(r => {
        const f = files[r.key];
        if (f?.music) fd.append(`${r.key}_music`, f.music);
        if (f?.content) fd.append(`${r.key}_content`, f.content);
      });
      await axios.post(
        `${API}/public/competition/${pageId}/music/submit`,
        fd,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000,
        }
      );
      toast.success(detail.content_sheet_required
        ? 'Your music and content sheets have been submitted. Thanks!'
        : 'Your music has been submitted. Thanks!');
      // Reset the form
      setClubId('');
      setEntryId('');
      setDetail(null);
      setFiles({});
      setEntries([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed — please try again.');
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="music-submission-form">
      {/* Club */}
      <div>
        <Label htmlFor="ms-club">Skater's Club *</Label>
        <select
          id="ms-club"
          className="w-full border rounded-lg px-3 py-2 mt-1 bg-white disabled:bg-stone-100 disabled:text-stone-500 disabled:cursor-not-allowed"
          value={clubId}
          onChange={(e) => setClubId(e.target.value)}
          disabled={clubs.length === 0}
          data-testid="ms-club-select"
        >
          <option value="">
            {clubs.length === 0 ? 'Waiting on competition entries…' : 'Select a club…'}
          </option>
          {clubs.map(c => (
            <option key={c.club_id} value={c.club_id}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Skater / pair / team */}
      <div>
        <div className="flex items-center gap-1.5">
          <Label htmlFor="ms-entry">Skater's Name *</Label>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Help"
                  className="text-stone-500 hover:text-stone-700"
                  data-testid="ms-entry-help"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                Only confirmed entries appear here. If your skater isn't listed, confirm
                your entry first from Skaters & Entries.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <select
          id="ms-entry"
          className="w-full border rounded-lg px-3 py-2 mt-1 bg-white disabled:bg-stone-50 disabled:text-stone-400"
          value={entryId}
          onChange={(e) => setEntryId(e.target.value)}
          disabled={!clubId}
          data-testid="ms-entry-select"
        >
          <option value="">
            {clubId ? (entries.length === 0 ? 'No confirmed entries for this club' : 'Select an entry…') : 'Choose a club first'}
          </option>
          {entries.map(e => (
            <option key={e.entry_id} value={e.entry_id}>
              {`${e.label} — ${e.event_name || e.grade_name}`}
            </option>
          ))}
        </select>
      </div>

      {/* Event (grade + discipline label; comes from the entry) */}
      {detail && !loadingDetail && (
        <div>
          <Label>Event</Label>
          <div
            className="w-full border rounded-lg px-3 py-2 mt-1 bg-stone-50 text-stone-700 text-sm"
            data-testid="ms-event-display"
          >
            {detail.grade_name} {detail.discipline_name ? `— ${detail.discipline_name}` : ''}
          </div>
        </div>
      )}

      {/* Per-round upload fields */}
      {detail && (detail.rounds || []).map(r => (
        <div
          key={r.key}
          className="border rounded-lg p-4 bg-stone-50/50"
          data-testid={`ms-round-${r.key}`}
        >
          <h3 className="font-semibold text-stone-800 mb-3">{r.content_type}</h3>
          <div className={`grid grid-cols-1 gap-3 ${detail.content_sheet_required ? 'md:grid-cols-2' : ''}`}>
            <div>
              <Label className="flex items-center gap-1.5 mb-1">
                <FileAudio className="h-4 w-4 text-stone-600" />
                Music (*.mp3 only)
              </Label>
              <Input
                type="file"
                accept=".mp3,audio/mpeg"
                onChange={(e) => updateFile(r.key, 'music', e.target.files?.[0] || null)}
                data-testid={`ms-${r.key}-music`}
              />
              {files[r.key]?.music && (
                <p className="text-xs text-stone-600 mt-1 truncate">
                  {files[r.key].music.name}
                </p>
              )}
            </div>
            {detail.content_sheet_required && (
            <div>
              <Label className="flex items-center gap-1.5 mb-1">
                <FilePdfIcon className="h-4 w-4 text-stone-600" />
                Content Sheet (*.pdf only)
              </Label>
              <Input
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => updateFile(r.key, 'content', e.target.files?.[0] || null)}
                data-testid={`ms-${r.key}-content`}
              />
              {files[r.key]?.content && (
                <p className="text-xs text-stone-600 mt-1 truncate">
                  {files[r.key].content.name}
                </p>
              )}
            </div>
            )}
          </div>
        </div>
      ))}

      {/* Submit */}
      {detail && (
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={!allUploaded || submitting}
          className="btn-primary w-full"
          data-testid="ms-submit-btn"
        >
          <Upload className="mr-2 h-4 w-4" />
          {submitting ? 'Submitting…' : (detail.content_sheet_required ? 'Submit Music & Content Sheets' : 'Submit Music')}
        </Button>
      )}

      {/* Confirm dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="ms-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Music className="h-5 w-5 text-[#2D4A3E]" />
              Ready to send?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Your files will be renamed and bundled into a single ZIP attachment, then
              emailed to the competition's music &amp; content address.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2 border-t border-b space-y-2 max-h-60 overflow-y-auto">
            <p className="text-sm">
              <span className="text-stone-500">Attachment:</span>{' '}
              <code className="text-[#2D4A3E] break-all font-semibold" data-testid="ms-zip-name">
                {detail?.zip_filename}
              </code>
            </p>
            <p className="text-xs text-stone-500 mt-2 mb-1">Contents:</p>
            <ul className="text-sm space-y-1 pl-4">
              {previewList.map((row, i) => (
                <li key={i} className="flex gap-2 py-0.5">
                  <span className="text-stone-500 w-28 shrink-0">{row.round} · {row.kind}:</span>
                  <code className="text-[#2D4A3E] break-all">{row.filename}</code>
                </li>
              ))}
            </ul>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ms-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runSubmit(); }}
              disabled={submitting}
              className="bg-[#2D4A3E] hover:bg-[#1f3329] text-white"
              data-testid="ms-confirm-send"
            >
              <Send className="mr-2 h-4 w-4" />
              {submitting ? 'Sending…' : 'Yes, send'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
